import { CSSProperties, useCallback } from 'react'

import { SxProps, TableCell, TableRow } from '@mui/material'
import { VirtualItem, Virtualizer } from '@tanstack/react-virtual'

import type { GRTableColumn } from '../GRTable'
import { HeaderCell } from '../HeaderCell/HeaderCell'
import { findParentColumn, getChildColumns, stickyColumnProps } from '../helpers/tableHelpers'
import './SectionColumnHeaderRow.scss'

/**
 * Component representing a repeated column header row rendered at the start of each expanded
 * section in a virtualized table (see repeatColumnHeadersPerSection prop of GRTable).
 *
 * Renders the label of each visible leaf column so the labels align with the data rows below.
 * The labels are non-interactive (no sorting).
 *
 * By default the cells adopt the column widths measured from the table's top header. When the
 * top header is hidden (see hideHeader prop of GRTable) this row takes over the measurement
 * instead: the row is laid out as a single row table spanning the full width and reports the
 * resulting cell widths through onMeasure.
 */

type SectionColumnHeaderRowProps<RowType, CustomPropsType, ColumnIdType = void> = {
  sectionId: string
  virtualRow: VirtualItem & { style?: CSSProperties }
  virtualizer: Virtualizer<Window, Element> | Virtualizer<Element, Element> | Virtualizer<HTMLElement, Element>
  columns: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]
  parentColumns?: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]
  getColumnHiddenValue: (col: GRTableColumn<RowType, CustomPropsType, ColumnIdType>) => boolean
  customTableProps?: CustomPropsType
  // renders an empty leading cell to compensate for the drag handle column when row sorting is enabled
  includeDragHandleCell?: boolean
  // when set, this row acts as the column width measurement source for the table
  onMeasure?: (column: GRTableColumn<RowType, CustomPropsType, ColumnIdType>, colWidth: number) => void
}

export const SectionColumnHeaderRow = <RowType extends object, CustomPropsType extends object | undefined, ColumnIdType extends string | void>({
  sectionId,
  virtualRow,
  virtualizer,
  columns,
  parentColumns = [],
  getColumnHiddenValue,
  customTableProps,
  includeDragHandleCell,
  onMeasure,
}: SectionColumnHeaderRowProps<RowType, CustomPropsType, ColumnIdType>) => {
  const childColumns = getChildColumns(columns)
  const isMeasuring = !!onMeasure

  const handleMeasure = useCallback(
    (colWidth: number, columnIndex: number) => {
      onMeasure?.(columns[columnIndex], colWidth)
    },
    [onMeasure, columns]
  )

  // descend to the leaf columns (the ones data cells are rendered for)
  if (childColumns.length > 0) {
    return (
      <SectionColumnHeaderRow
        sectionId={sectionId}
        virtualRow={virtualRow}
        virtualizer={virtualizer}
        columns={childColumns}
        parentColumns={columns}
        getColumnHiddenValue={getColumnHiddenValue}
        customTableProps={customTableProps}
        includeDragHandleCell={includeDragHandleCell}
        onMeasure={onMeasure}
      />
    )
  }

  const getHeaderValue = (col: GRTableColumn<RowType, CustomPropsType, ColumnIdType>) => {
    if (typeof col.labelAccessor === 'function') {
      return col.labelAccessor({ col, customTableProps, columns, sectionId })
    } else {
      return col.labelAccessor
    }
  }

  return (
    <TableRow
      className="SectionColumnHeaderRow"
      data-index={virtualRow.index}
      ref={(node) => virtualizer.measureElement(node)}
      style={{
        position: 'absolute',
        top: virtualRow.start - virtualizer.options.scrollMargin,
        // when measuring, lay the row out as a single row table spanning the full width so the
        // cells get the same widths the table's top header would produce
        ...(isMeasuring ? { display: 'table', width: '100%' } : {}),
      }}
    >
      {includeDragHandleCell && <TableCell variant="head" className="SectionColumnHeaderRow__cell" sx={{ px: 0, py: 1 }} style={{ width: 60 }} />}
      {columns.map((column, columnIndex) => {
        const parentColumn = findParentColumn(parentColumns, column)
        const isHidden = (parentColumn && getColumnHiddenValue(parentColumn)) || getColumnHiddenValue(column)
        const isStickyColumn = column.sticky || parentColumn?.sticky
        const cellProps = isStickyColumn
          ? { ...column.headerCellProps, sx: { ...column.headerCellProps?.sx, ...stickyColumnProps.sx } as SxProps }
          : column.headerCellProps

        return isHidden ? null : (
          <HeaderCell
            key={columnIndex}
            columnIndex={columnIndex}
            className="SectionColumnHeaderRow__cell"
            column={column}
            cellProps={{
              ...cellProps,
              // adopt the measured column widths unless this row is the measurement source itself
              style: isMeasuring ? cellProps?.style : { ...cellProps?.style, width: column.width, minWidth: column.width, maxWidth: column.width },
            }}
            onMeasure={isMeasuring ? handleMeasure : undefined}
          >
            {getHeaderValue(column)}
          </HeaderCell>
        )
      })}
    </TableRow>
  )
}
